'use client';

import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import styled, { css } from 'styled-components';
import { copyFontFamily, copyFontSize, copyFontSizeSmall, colorDarkest, colorWhite, colorLighter, standardFocusStyleDeclarations, formSelectedToggleColor } from '@nib-components/theme';
import { ChevronRightSystemIcon } from '@nib/icons';
import { Box, Columns, Column, Hidden, breakpoint } from '@nib/layout';
const truncatedTextStyles = css`
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const SubTitle = styled(_ref => {
  let {
    truncate,
    ...rest
  } = _ref;
  return /*#__PURE__*/_jsx("div", {
    ...rest
  });
})`
  font-family: ${copyFontFamily || 'sans-serif'};
  font-size: ${copyFontSizeSmall};
  word-break: break-word;
  hyphens: auto;

  ${breakpoint('mini')`
    hyphens: none;  
  `}
  ${_ref2 => {
  let {
    truncate
  } = _ref2;
  return truncate && truncatedTextStyles;
}};
`;
SubTitle.displayName = 'UtilityButton.SubTitle';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const Title = styled(_ref3 => {
  let {
    truncate,
    ...rest
  } = _ref3;
  return /*#__PURE__*/_jsx("div", {
    ...rest
  });
})`
  font-family: ${copyFontFamily || 'sans-serif'};
  font-size: ${copyFontSize};
  font-weight: 700;
  word-break: break-word;
  hyphens: auto;

  ${breakpoint('mini')`
    hyphens: none;  
  `}
  ${_ref4 => {
  let {
    truncate
  } = _ref4;
  return truncate && truncatedTextStyles;
}};
`;
Title.displayName = 'UtilityButton.Title';
const Wrapper = styled(Box)`
  display: block;
  cursor: pointer;
  background: var(--themeColorBgSurface, ${colorWhite});
  color: var(--themeColorFg, ${colorDarkest});
  text-decoration: none;
  border: none;
  line-height: inherit;
  text-align: start;
  width: 100%;
  transition: transform 0.125s ease;

  ${_ref5 => {
  let {
    showBorder
  } = _ref5;
  return showBorder && css`
      box-shadow: inset 0 0 0 1px var(--themeColorBorder, ${colorLighter});
    `;
}}

  ${_ref6 => {
  let {
    selected
  } = _ref6;
  return selected && css`
      ${UtilityButton.Title} {
        color: var(--themeColorFgSelected, ${formSelectedToggleColor});
      }
      box-shadow: inset 0 0 0 2px var(--themeColorBorderSelected, ${formSelectedToggleColor});
    `;
}}

  &:hover, 
  &:focus-visible {
    transform: translateY(-0.1rem);
  }

  &:focus-visible {
    ${standardFocusStyleDeclarations};
  }

  svg {
    display: block;
  }

  ${_ref7 => {
  let {
    disabled
  } = _ref7;
  return disabled && css`
      opacity: 0.4;

      &:hover,
      &:focus-visible {
        background: var(--themeColorBg, ${colorWhite});
        cursor: not-allowed;
      }
    `;
}}
`;
const FullHeightColumns = styled(Columns)`
  height: 100%;
`;
export const DirectionAwareChevronSystemIcon = styled(ChevronRightSystemIcon)`
  &:dir(rtl) {
    transform: scaleX(-1);
  }
`;
const UtilityButton = props => {
  const {
    icon: Icon,
    actionIcon: ActionIcon = DirectionAwareChevronSystemIcon,
    showBorder = true,
    selected = false,
    showActionIcon = true,
    href,
    disabled = false,
    onClick,
    component,
    children,
    ...otherProps
  } = props;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const element = component ? component : onClick ? 'button' : 'a';
  return /*#__PURE__*/_jsx(Wrapper, {
    paddingVertical: 3,
    paddingHorizontal: {
      xs: 3,
      mini: 4
    },
    borderRadius: "standard",
    selected: selected,
    showBorder: showBorder,
    as: element,
    href: href && !disabled ? href : null,
    disabled: disabled,
    onClick: onClick,
    ...otherProps,
    "data-mesh-component": "UTILITY-BUTTON",
    children: /*#__PURE__*/_jsxs(FullHeightColumns, {
      space: {
        xs: 3,
        mini: 4
      },
      verticalAlign: "center",
      collapseBelow: "mini",
      children: [Icon && /*#__PURE__*/_jsx(Column, {
        width: "content",
        children: /*#__PURE__*/_jsx(Icon, {
          size: "md",
          fill: "brand"
        })
      }), /*#__PURE__*/_jsx(Column, {
        children: children
      }), /*#__PURE__*/_jsx(Hidden, {
        below: "mini",
        children: showActionIcon && ActionIcon && /*#__PURE__*/_jsx(Column, {
          width: "content",
          children: /*#__PURE__*/_jsx(ActionIcon, {
            size: "sm",
            fill: "gentle"
          })
        })
      })]
    })
  });
};
UtilityButton.displayName = 'UtilityButton';
UtilityButton.Title = Title;
UtilityButton.SubTitle = SubTitle;
export default UtilityButton;
export { Title as UtilityButtonTitle, SubTitle as UtilityButtonSubTitle };